import { DefaultButton } from "@/components/DefaultButton";
import { Input } from "@/components/ui/input";
import {
  bridgeDebugLog,
  installBridgeDebugInstrumentation,
  isBridgeDebugEnabled,
} from "@/lib/debug/bridgeDebug";
import configs from "@/lib/configs";
import { usePrivyCustom } from "@/lib/hooks/usePrivyCustom";
import { usePrivy } from "@privy-io/react-auth";
import { createFileRoute } from "@tanstack/react-router";
import { MiniKit } from "@worldcoin/minikit-js";
import { useMiniKit } from "@worldcoin/minikit-js/minikit-provider";
import * as React from "react";
import { toast } from "sonner";
import { encodeFunctionData, erc20Abi, formatUnits, parseUnits } from "viem";
import { useConnection, useConnect, useConnectors } from "wagmi";

const WORLD_CHAIN_ID = 480;
const WORLD_CHAIN_USDC = "0x79a02482a880bce3f13e09da970dc34db4cd24d1";
const USDC_DECIMALS = 6;
const HARDCODED_RECIPIENT = "0x2318A928F22a831286629c868077977aFe174A3e";
const DEFAULT_USDC_AMOUNT = "0.1";
const ZERO_BIG_INT = BigInt(0);
const COMMAND_TIMEOUT_MS = 15000;

type TransferMode = "raw" | "abi" | "abi-no-format";

export const Route = createFileRoute("/relay-test")({
  component: RelayTestRoute,
});

function RelayTestRoute() {
  const { user: privyUser } = usePrivy();
  const { login } = usePrivyCustom();
  const { isInstalled } = useMiniKit();
  const { connector: activeConnector, address: wagmiAddress } = useConnection();
  const connectors = useConnectors();
  const { mutateAsync: connectAsync } = useConnect();

  const [usdcAmount, setUsdcAmount] = React.useState(DEFAULT_USDC_AMOUNT);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [runningTransferMode, setRunningTransferMode] =
    React.useState<TransferMode | null>(null);
  const [testResult, setTestResult] = React.useState<string>();
  const [runningProbe, setRunningProbe] = React.useState<string | null>(null);
  const [controlResult, setControlResult] = React.useState<string>();
  const [installProbeResult, setInstallProbeResult] = React.useState<string>();
  const [diagnostics, setDiagnostics] = React.useState(() =>
    getMiniKitDiagnostics(isInstalled),
  );

  React.useEffect(() => {
    installBridgeDebugInstrumentation();
  }, []);

  React.useEffect(() => {
    setDiagnostics(getMiniKitDiagnostics(isInstalled));
  }, [isInstalled]);

  const evmAddress = privyUser?.wallet?.address ?? wagmiAddress;
  const configuredAppId = configs.WORLD_MINI_APP.ID;

  const connectWorldAppWallet = React.useCallback(async () => {
    const preferredConnectorId = MiniKit.isInWorldApp()
      ? "worldApp"
      : "injected";
    const connector =
      connectors.find((item) => item.id === preferredConnectorId) ??
      connectors.find((item) => item.id === "injected");

    if (!connector) {
      await login();
      return;
    }

    try {
      bridgeDebugLog("relay-test", "connect:start", {
        connectorId: connector.id,
      });

      const connection = await connectAsync({ connector });

      bridgeDebugLog("relay-test", "connect:success", {
        connectorId: connector.id,
        address: connection.accounts[0],
      });
    } catch (error) {
      bridgeDebugLog("relay-test", "connect:error", error);
      console.error("Failed to connect wallet:", error);
      toast.error("Failed to connect wallet");
    }
  }, [connectAsync, connectors, login]);

  const parsedAmount = React.useMemo(() => {
    try {
      const normalized = usdcAmount.trim();

      if (!normalized) {
        return null;
      }

      return parseUnits(normalized, USDC_DECIMALS);
    } catch {
      return null;
    }
  }, [usdcAmount]);

  const transferCalldata = React.useMemo(() => {
    if (parsedAmount === null) {
      return null;
    }

    return encodeFunctionData({
      abi: erc20Abi,
      functionName: "transfer",
      args: [HARDCODED_RECIPIENT, parsedAmount],
    });
  }, [parsedAmount]);

  const executeUsdcTransfer = React.useCallback(
    async (mode: TransferMode) => {
      const currentDiagnostics = getMiniKitDiagnostics(isInstalled);

      setDiagnostics(currentDiagnostics);

      if (!currentDiagnostics.inWorldApp) {
        throw new Error("This test must run inside World App");
      }

      if (!currentDiagnostics.isInstalled) {
        throw new Error("MiniKitProvider is not installed for this route");
      }

      if (!currentDiagnostics.hasNativeBridge) {
        throw new Error(
          "World App was detected, but the native MiniKit bridge is missing. This usually means the page is not running inside the native World App webview.",
        );
      }

      if (!currentDiagnostics.sendTransactionSupported) {
        throw new Error(
          "World App does not report send-transaction support for this session",
        );
      }

      if (parsedAmount === null) {
        throw new Error("Enter a valid USDC amount");
      }

      if (parsedAmount <= ZERO_BIG_INT) {
        throw new Error("USDC amount must be greater than zero");
      }

      if (!transferCalldata) {
        throw new Error("Failed to build transfer calldata");
      }

      const payload = {
        chainId: WORLD_CHAIN_ID,
        transactions: [
          {
            to: WORLD_CHAIN_USDC,
            data: transferCalldata,
          },
        ],
      };

      bridgeDebugLog("relay-test", "usdc-transfer:payload", {
        ...payload,
        mode,
        amount: usdcAmount.trim(),
        amountBaseUnits: parsedAmount.toString(),
        recipient: HARDCODED_RECIPIENT,
        sender: evmAddress ?? null,
        diagnostics: currentDiagnostics,
      });

      return MiniKit.sendTransaction(payload);
    },
    [evmAddress, isInstalled, parsedAmount, transferCalldata, usdcAmount],
  );

  const runTransferTest = React.useCallback((mode: TransferMode) => {
    setIsSubmitting(true);
    setRunningTransferMode(mode);
    setTestResult(
      `${mode} transfer started at ${new Date().toISOString()}`,
    );

    const timeoutPromise = new Promise<never>((_, reject) => {
      window.setTimeout(() => {
        reject(
          new Error(
            `MiniKit sendTransaction (${mode}) timed out after ${COMMAND_TIMEOUT_MS / 1000}s waiting for the native callback. If no World App confirmation drawer appeared, the native bridge likely never responded.`,
          ),
        );
      }, COMMAND_TIMEOUT_MS);
    });

    void Promise.race([executeUsdcTransfer(mode), timeoutPromise])
      .then((result) => {
        const resultText = JSON.stringify(result, null, 2);

        setTestResult(resultText);
        toast.success(`USDC transfer submitted (${mode})`);
        bridgeDebugLog("relay-test", `usdc-transfer:${mode}:success`, result);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);

        setTestResult(message);
        toast.error(message);
        bridgeDebugLog("relay-test", `usdc-transfer:${mode}:error`, error);
      })
      .finally(() => {
        setIsSubmitting(false);
        setRunningTransferMode(null);
      });
  }, [executeUsdcTransfer]);

  const refreshDiagnostics = React.useCallback(() => {
    const installResult = MiniKit.install(configuredAppId);
    const installResultText = JSON.stringify(installResult, null, 2);

    setInstallProbeResult(installResultText);
    setDiagnostics(getMiniKitDiagnostics(isInstalled));
    bridgeDebugLog("relay-test", "install:probe", {
      configuredAppId,
      installResult,
    });
  }, [configuredAppId, isInstalled]);

  const runControlProbe = React.useCallback(
    async (
      probeId: "haptic" | "permissions",
      label: string,
      executor: () => Promise<unknown>,
    ) => {
      setRunningProbe(probeId);
      setControlResult(`${label} started at ${new Date().toISOString()}`);

      const timeoutPromise = new Promise<never>((_, reject) => {
        window.setTimeout(() => {
          reject(
            new Error(
              `${label} timed out after ${COMMAND_TIMEOUT_MS / 1000}s waiting for a MiniKit callback.`,
            ),
          );
        }, COMMAND_TIMEOUT_MS);
      });

      void Promise.race([executor(), timeoutPromise])
        .then((result) => {
          const resultText = JSON.stringify(result, null, 2);

          setControlResult(resultText);
          toast.success(`${label} completed`);
          bridgeDebugLog("relay-test", `${probeId}:success`, result);
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);

          setControlResult(message);
          toast.error(message);
          bridgeDebugLog("relay-test", `${probeId}:error`, error);
        })
        .finally(() => {
          setRunningProbe(null);
        });
    },
    [],
  );

  const handleHapticProbe = React.useCallback(async () => {
    bridgeDebugLog("relay-test", "haptic:payload", {
      hapticsType: "impact",
      style: "light",
    });

    return MiniKit.sendHapticFeedback({
      hapticsType: "impact",
      style: "light",
    });
  }, []);

  const handlePermissionsProbe = React.useCallback(async () => {
    bridgeDebugLog("relay-test", "permissions:payload", {});
    return MiniKit.getPermissions();
  }, []);

  const connectionSummary = [
    `In World App: ${MiniKit.isInWorldApp() ? "yes" : "no"}`,
    `MiniKit installed: ${diagnostics.isInstalled ? "yes" : "no"}`,
    `Bridge debug: ${isBridgeDebugEnabled() ? "enabled" : "disabled"}`,
    `Native bridge: ${diagnostics.hasNativeBridge ? "yes" : "no"}`,
    `send-transaction support: ${diagnostics.sendTransactionSupported ? "yes" : "no"}`,
    `Connector: ${activeConnector?.id ?? "none"}`,
    `Connected wallet: ${evmAddress ?? "none"}`,
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <main className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">MiniKit USDC Transfer Test</h1>
          <p className="max-w-3xl text-sm text-zinc-400">
            This page sends a direct World Chain USDC transfer with{" "}
            <code>MiniKit.sendTransaction</code> using MiniKit v2 transaction
            payloads. The recipient is fixed so you can repeatedly test the same
            wallet-to-wallet flow inside World App.
          </p>
        </div>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <h2 className="text-lg font-medium">Wallet Setup</h2>
              <div className="space-y-1 text-sm text-zinc-400">
                {connectionSummary.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <DefaultButton
                className="h-10"
                onClick={() => {
                  refreshDiagnostics();
                }}
              >
                Refresh Diagnostics
              </DefaultButton>
              <DefaultButton
                className="h-10"
                onClick={() => {
                  void connectWorldAppWallet();
                }}
              >
                Connect World App Wallet
              </DefaultButton>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <InfoCard
              label="World App Version"
              value={diagnostics.worldAppVersion ?? "unknown"}
            />
            <InfoCard
              label="Runtime App ID"
              value={diagnostics.appId ?? "unknown"}
              monospace
            />
            <InfoCard
              label="Configured App ID"
              value={configuredAppId ?? "unknown"}
              monospace
            />
            <InfoCard
              label="Native Transport"
              value={diagnostics.nativeTransportLabel}
            />
            <InfoCard
              label="send-transaction Versions"
              value={diagnostics.sendTransactionVersions ?? "not reported"}
            />
          </div>

          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-400">
            <div className="font-medium text-zinc-300">
              MiniKit.install({configuredAppId ? "appId" : "undefined"}) Probe
            </div>
            <div className="mt-2 break-all whitespace-pre-wrap">
              {installProbeResult ??
                'Tap "Refresh Diagnostics" to run MiniKit.install(...) and capture its return value.'}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4">
          <div className="mb-4 space-y-1">
            <h2 className="text-lg font-medium">Control Commands</h2>
            <p className="text-sm text-zinc-400">
              Use these to verify whether MiniKit can round-trip a non-transaction
              command callback in this same World App session.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <DefaultButton
              className="h-10"
              disabled={runningProbe !== null}
              onClick={() => {
                void runControlProbe(
                  "haptic",
                  "sendHapticFeedback",
                  handleHapticProbe,
                );
              }}
            >
              {runningProbe === "haptic"
                ? "Running Haptic Probe..."
                : "Run Haptic Probe"}
            </DefaultButton>
            <DefaultButton
              className="h-10"
              disabled={runningProbe !== null}
              onClick={() => {
                void runControlProbe(
                  "permissions",
                  "getPermissions",
                  handlePermissionsProbe,
                );
              }}
            >
              {runningProbe === "permissions"
                ? "Running Permissions Probe..."
                : "Run Permissions Probe"}
            </DefaultButton>
          </div>

          <TestResult value={controlResult} />
        </section>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4">
          <div className="mb-4 space-y-1">
            <h2 className="text-lg font-medium">Direct USDC Transfer</h2>
            <p className="text-sm text-zinc-400">
              The transaction calls ERC-20 <code>transfer(address,uint256)</code>{" "}
              on World Chain USDC and returns the MiniKit v2 response shape with{" "}
              <code>executedWith</code> and <code>data</code>.
            </p>
            <p className="text-sm text-amber-300">
              If this stalls without opening a World App drawer, the page will
              fail after {COMMAND_TIMEOUT_MS / 1000}s and show the detected
              bridge state below.
            </p>
            <p className="text-sm text-zinc-500">
              Try all three payload modes. World’s docs note that setting{" "}
              <code>formatPayload</code> to <code>false</code> can help when a
              transaction payload gets stuck on validation.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <InfoCard
              label="Network"
              value={`World Chain (${WORLD_CHAIN_ID})`}
            />
            <InfoCard label="Token Contract" value={WORLD_CHAIN_USDC} monospace />
            <InfoCard
              label="Recipient"
              value={HARDCODED_RECIPIENT}
              monospace
            />
            <InfoCard
              label="Amount Preview"
              value={
                parsedAmount === null
                  ? "Invalid amount"
                  : `${formatUnits(parsedAmount, USDC_DECIMALS)} USDC`
              }
            />
          </div>

          <div className="mt-4 space-y-2">
            <label
              htmlFor="relay-test-usdc-amount"
              className="text-sm font-medium text-zinc-300"
            >
              USDC amount
            </label>
            <Input
              id="relay-test-usdc-amount"
              value={usdcAmount}
              onChange={(event) => setUsdcAmount(event.target.value)}
              inputMode="decimal"
              placeholder="0.1"
              className="border-zinc-700 bg-zinc-950 text-zinc-100"
            />
            <p className="text-xs text-zinc-500">
              USDC uses {USDC_DECIMALS} decimals on World Chain.
            </p>
          </div>

          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-400">
            <div className="font-medium text-zinc-300">Calldata Preview</div>
            <div className="mt-2 break-all">
              {transferCalldata ?? "Enter a valid USDC amount to build calldata."}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <DefaultButton
              className="h-10"
              disabled={isSubmitting || parsedAmount === null}
              onClick={() => {
                runTransferTest("raw");
              }}
            >
              {runningTransferMode === "raw"
                ? "Running Raw..."
                : "Run Raw Calldata"}
            </DefaultButton>
            <DefaultButton
              className="h-10"
              disabled={isSubmitting || parsedAmount === null}
              onClick={() => {
                runTransferTest("abi");
              }}
            >
              {runningTransferMode === "abi"
                ? "Running ABI..."
                : "Run ABI Payload"}
            </DefaultButton>
            <DefaultButton
              className="h-10"
              disabled={isSubmitting || parsedAmount === null}
              onClick={() => {
                runTransferTest("abi-no-format");
              }}
            >
              {runningTransferMode === "abi-no-format"
                ? "Running ABI No Format..."
                : "Run ABI + No Format"}
            </DefaultButton>
          </div>

          <TestResult value={testResult} />
        </section>
      </main>
    </div>
  );
}

function InfoCard({
  label,
  value,
  monospace = false,
}: {
  label: string;
  value: string;
  monospace?: boolean;
}) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </div>
      <div
        className={`mt-2 text-sm text-zinc-200 ${
          monospace ? "break-all font-mono" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

type SupportedCommand = {
  name?: string;
  supported_versions?: number[];
};

function getMiniKitDiagnostics(isInstalled?: boolean) {
  if (typeof window === "undefined") {
    return {
      inWorldApp: false,
      isInstalled: Boolean(isInstalled),
      hasNativeBridge: false,
      hasAndroidBridge: false,
      hasWebkitBridge: false,
      nativeTransportLabel: "Unavailable",
      sendTransactionSupported: false,
      sendTransactionVersions: null as string | null,
      worldAppVersion: null as string | null,
      appId: null as string | null,
    };
  }

  const scopedWindow = window as Window &
    typeof globalThis & {
      WorldApp?: {
        world_app_version?: string | number;
        supported_commands?: SupportedCommand[];
      };
      webkit?: {
        messageHandlers?: {
          minikit?: {
            postMessage?: (payload: unknown) => void;
          };
        };
      };
      Android?: {
        postMessage?: (payload: string) => void;
      };
    };

  const supportedCommands = scopedWindow.WorldApp?.supported_commands ?? [];
  const sendTransactionCommand = supportedCommands.find(
    (command) => command.name === "send-transaction",
  );
  const hasWebkitBridge = Boolean(
    scopedWindow.webkit?.messageHandlers?.minikit?.postMessage,
  );
  const hasAndroidBridge = Boolean(scopedWindow.Android?.postMessage);
  const hasNativeBridge = hasWebkitBridge || hasAndroidBridge;

  return {
    inWorldApp: MiniKit.isInWorldApp(),
    isInstalled: Boolean(isInstalled),
    hasNativeBridge,
    hasAndroidBridge,
    hasWebkitBridge,
    nativeTransportLabel: hasWebkitBridge
      ? "iOS webkit.messageHandlers.minikit"
      : hasAndroidBridge
        ? "Android.postMessage"
        : "Missing",
    sendTransactionSupported: Boolean(sendTransactionCommand),
    sendTransactionVersions:
      sendTransactionCommand?.supported_versions?.join(", ") ?? null,
    worldAppVersion: scopedWindow.WorldApp?.world_app_version
      ? String(scopedWindow.WorldApp.world_app_version)
      : null,
    appId: MiniKit.appId ?? null,
  };
}

function TestResult({ value }: { value?: string }) {
  if (!value) {
    return null;
  }

  return (
    <pre className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950/80 p-3 text-xs text-zinc-300">
      {value}
    </pre>
  );
}
